// ABOUTME: History sidebar showing past investigation sessions.
// ABOUTME: Allows creating new investigations and loading existing ones.

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, MessageSquare, Trash2, Loader2 } from 'lucide-react';
import type { InvestigationSession } from '@/types';

interface HistorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  sessions: InvestigationSession[];
  currentSessionId: string | null;
  onNewSession: () => Promise<string>;
  onLoadSession: (id: string) => void;
  onDeleteSession: (id: string) => Promise<void>;
}

export function HistorySidebar({
  isOpen,
  onClose,
  sessions,
  currentSessionId,
  onNewSession,
  onLoadSession,
  onDeleteSession,
}: HistorySidebarProps) {
  const [isCreating, setIsCreating] = useState(false);

  const handleNewSession = async () => {
    setIsCreating(true);
    try {
      await onNewSession();
      onClose();
    } catch (err) {
      console.error('Failed to create session:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (e: React.SyntheticEvent, id: string) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this investigation?')) {
      await onDeleteSession(id);
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[9998]"
            onClick={onClose}
          />

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed top-0 left-0 h-full w-80 bg-surface-0 border-r border-surface-200 p-6 flex flex-col z-[9999] shadow-xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-surface-800 font-semibold tracking-tight">History</h2>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-surface-100 rounded-sm transition-colors cursor-pointer"
              >
                <X className="w-4 h-4 text-surface-400 hover:text-surface-600" />
              </button>
            </div>

            {/* New Investigation Button */}
            <button
              onClick={handleNewSession}
              disabled={isCreating}
              className="flex items-center gap-2 w-full p-3 rounded-sm bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium mb-6 transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
            >
              {isCreating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              {isCreating ? 'Creating...' : 'New Investigation'}
            </button>

            {/* Sessions List */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {sessions.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-8 h-8 text-surface-400 mx-auto mb-3" />
                  <p className="text-sm text-surface-500">
                    No investigations yet
                  </p>
                </div>
              ) : (
                [...sessions]
                  .sort((a, b) => b.lastActive.getTime() - a.lastActive.getTime())
                  .map((session) => (
                  <button
                    key={session.id}
                    onClick={() => {
                      onLoadSession(session.id);
                      onClose();
                    }}
                    className={`
                      w-full text-left p-3 rounded-sm text-sm transition-all group cursor-pointer
                      ${session.id === currentSessionId
                        ? 'bg-primary-50 text-surface-800 shadow-sm border border-primary-200'
                        : 'text-surface-600 hover:bg-surface-100'}
                    `}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{session.title}</div>
                        <div className="text-[10px] text-surface-400 mt-1">
                          {formatDate(session.lastActive)}
                        </div>
                        {session.messages.length > 0 && (
                          <div className="text-[11px] text-surface-400 mt-1 line-clamp-1">
                            {session.messages.length} message{session.messages.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleDelete(e, session.id)}
                        onKeyDown={(e) => e.key === 'Enter' && handleDelete(e, session.id)}
                        className="p-1 rounded-sm opacity-0 group-hover:opacity-100 hover:bg-error-light transition-all cursor-pointer"
                        aria-label="Delete investigation"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-surface-400 hover:text-error" />
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

// Helper to format date
function formatDate(date: Date): string {
  const now = new Date();
  const d = new Date(date);
  const diff = now.getTime() - d.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return d.toLocaleDateString();
}
